import {Component, OnInit} from '@angular/core';
import {
  FeeSnapshot,
  FeeSnapshotItem,
  normalizeFeeSnapshotResponse,
  toEffectiveFeeSnapshot,
} from '@gatewee/types/fee';
import {Observable, catchError} from 'rxjs';
import {map} from 'rxjs/operators';
import {readProfileRaw} from '@gatewee/services';
import {FeesService} from '../../_services/fees/fees.service';

@Component({
  selector: 'app-my-fees',
  templateUrl: './my-fees.component.html',
  standalone: false,
})
export class MyFeesComponent implements OnInit {
  feeSnapshot: FeeSnapshot | null = null;
  isLoading = true;
  hasError = false;

  constructor(private readonly feesService: FeesService) {}

  ngOnInit(): void {
    this.loadFees();
  }

  get hasAssociatedFees(): boolean {
    const domainCount = this.feeSnapshot?.domain?.length ?? 0;
    const recipientCount = this.feeSnapshot?.recipient?.length ?? 0;
    return domainCount + recipientCount > 0;
  }

  private loadFees(): void {
    this.isLoading = true;
    this.hasError = false;

    this.feesService
      .findFeeSnapshotRecipient()
      .pipe(
        map(items => normalizeFeeSnapshotResponse(items)),
        catchError(() => this.resolveFeeSnapshotLegacy()),
      )
      .subscribe({
        next: snapshot => {
          this.feeSnapshot = snapshot;
          this.isLoading = false;
        },
        error: () => {
          this.hasError = true;
          this.feeSnapshot = null;
          this.isLoading = false;
        },
      });
  }

  /** Fallback when GET /fees/recipient/snapshot is unavailable. */
  private resolveFeeSnapshotLegacy(): Observable<FeeSnapshot> {
    const recipientId = this.getRecipientIdFromProfile();

    if (recipientId) {
      return this.feesService.findFeeSnapshot(recipientId).pipe(
        map(raw => normalizeFeeSnapshotResponse(raw)),
        catchError(() => this.findDefaultAsSnapshot()),
      );
    }

    return this.findDefaultAsSnapshot();
  }

  private findDefaultAsSnapshot(): Observable<FeeSnapshot> {
    return this.feesService
      .findDefaultFees()
      .pipe(map((fees: FeeSnapshotItem[]) => toEffectiveFeeSnapshot(fees)));
  }

  private getRecipientIdFromProfile(): number | null {
    try {
      const profile = JSON.parse(readProfileRaw()) as {
        recipient?: {id?: number};
      };
      const id = profile?.recipient?.id;
      return typeof id === 'number' && id > 0 ? id : null;
    } catch {
      return null;
    }
  }
}
